/**
 * Banner 1061 — KUK programme hero.
 *
 * Slides are `metadata`, and each carries its own LAYOUT as well as its own copy:
 * v1 hardcodes three different overlays by slide index, so the layout has to be a
 * field or the third slide can never be edited. The accreditation badges are
 * `metadata_1`, and each slide chooses whether to show them.
 *
 * Every field maps to a column that already exists on `banner` — the variant adds no
 * schema of its own beyond widening `banner.variant`.
 */

export const BANNER_1061_CONSTANTS = {
  METADATA_TITLES: {
    metadata: "Hero slides",
    metadata_1: "Accreditation badges",
  },

  DEFAULT_ROW: {
    id: "1",
    image: "",
    alt_image: "",
    mobile_image: "",
    mobile_alt_image: "",
    layout: "left_card",
    scrim_color: "rgba(0, 0, 0, 0.5)",
    panel_color: "rgba(255, 255, 255, 0.2)",
    panel_color_mobile: "",
    panel_width: 40,
    title_1: "",
    title_1_color: "#FFFFFF",
    title: "",
    title_color: "#FFFFFF",
    description: "",
    description_color: "#FFFFFF",
    chips: "",
    chip_color: "#EFAC6A",
    chip_text_color: "#031F44",
    info_bar: "",
    info_bar_color: "rgba(255, 255, 255, 0.16)",
    info_bar_text_color: "#FFFFFF",
    cta_background_color: "",
    cta_text_color: "",
    brochure_background_color: "",
    brochure_text_color: "",
    show_accreditation: true,
    priority: 1,
    status: true,
  },

  DEFAULT_ROW_1: {
    id: "1",
    meta_image: "",
    meta_alt_image: "",
    meta_title: "",
    meta_priority: 1,
    meta_status: true,
  },

  BANNER_FIELDS_CONFIG: [
    {
      fieldName: "slide_frequency",
      label: "Slide interval (ms)",
      value: 5000,
      valueType: "number",
      filedType: "INPUT",
      placeholder: "5000",
      defaultValue: 5000,
    },
    {
      fieldName: "hero_section_height",
      label: "Desktop height (px)",
      value: 0,
      valueType: "number",
      filedType: "INPUT",
      placeholder: "0",
      description: "0 keeps the reference's viewport-relative height (75vh / 90vh on a short window).",
      defaultValue: 0,
    },
    {
      fieldName: "background_color",
      label: "Accreditation rail fill",
      value: "rgba(255, 255, 255, 0.3)",
      valueType: "string",
      filedType: "RGBA",
      placeholder: "rgba(255, 255, 255, 0.3)",
      defaultValue: "rgba(255, 255, 255, 0.3)",
    },
    {
      fieldName: "title_color",
      label: "Accreditation label colour",
      value: "#031F44",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#031F44",
      defaultValue: "#031F44",
    },
    {
      fieldName: "divider_color",
      label: "Accreditation divider colour",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#FFFFFF",
      description: "The rule between badges — a different colour from the label in the reference.",
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "carousel_color",
      label: "Prev / next arrow fill",
      value: "rgba(0, 0, 0, 0.5)",
      valueType: "string",
      filedType: "RGBA",
      placeholder: "rgba(0, 0, 0, 0.5)",
      defaultValue: "rgba(0, 0, 0, 0.5)",
    },
    {
      fieldName: "apply_now_show",
      label: "Show the Apply button",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
    {
      fieldName: "apply_now_text",
      label: "Apply button text",
      value: "Apply",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Apply",
      defaultValue: "Apply",
    },
    {
      fieldName: "apply_now_is_cta",
      label: "Apply opens the lead form",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      description: "Off → link to the URL below instead.",
      defaultValue: true,
    },
    {
      fieldName: "apply_now_link",
      label: "Apply link (when it is not a lead form)",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "/admission-portal",
      defaultValue: "",
    },
    {
      fieldName: "button_color",
      label: "Apply fill (slides that set none of their own)",
      value: "#9F1C33",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#9F1C33",
      defaultValue: "#9F1C33",
    },
    {
      fieldName: "apply_now_color",
      label: "CTA label colour (slides that set none of their own)",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#FFFFFF",
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "download_brochure_show",
      label: "Show the Brochure button",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
    {
      fieldName: "download_brochure_text",
      label: "Brochure button text",
      value: "Brochure",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Brochure",
      defaultValue: "Brochure",
    },
    {
      fieldName: "download_brochure_is_cta",
      label: "Brochure opens the download lead form",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      description: "Off → link straight to the PDF below.",
      defaultValue: true,
    },
    {
      fieldName: "download_brochure_link",
      label: "Brochure PDF (when it is not a lead form)",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload or paste a PDF URL",
      defaultValue: "",
    },
    {
      fieldName: "apply_now_border_color",
      label: "Brochure fill (slides that set none of their own)",
      value: "rgba(255, 255, 255, 0.34)",
      valueType: "string",
      filedType: "RGBA",
      placeholder: "rgba(255, 255, 255, 0.34)",
      defaultValue: "rgba(255, 255, 255, 0.34)",
    },
    {
      fieldName: "icon",
      label: "Brochure button icon",
      value: "download",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload an icon, or type an icon name",
      description: "Accepts an uploaded image URL OR a built-in icon name such as download.",
      defaultValue: "download",
    },
    {
      fieldName: "status",
      label: "Banner status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
  ],

  METADATA_FIELDS: [
    {
      header: "Layout",
      fieldName: "layout",
      value: "left_card",
      valueType: "string",
      filedType: "SELECT",
      options: [
        { label: "Left card (title + accreditation rail)", value: "left_card" },
        { label: "Right panel (chips + centred heading)", value: "right_panel" },
        { label: "Wide scrim (chips + left-aligned copy)", value: "wide_scrim" },
      ],
      defaultValue: "left_card",
    },
    {
      header: "Slide background (desktop)",
      fieldName: "image",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Desktop background image",
      defaultValue: "",
    },
    {
      header: "Background alt (desktop)",
      fieldName: "alt_image",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "",
    },
    {
      header: "Slide background (mobile)",
      fieldName: "mobile_image",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Optional — uses the desktop image if empty",
      defaultValue: "",
    },
    {
      header: "Background alt (mobile)",
      fieldName: "mobile_alt_image",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "",
    },
    {
      header: "Scrim over the artwork (rgba)",
      fieldName: "scrim_color",
      value: "rgba(0, 0, 0, 0.5)",
      valueType: "string",
      filedType: "RGBA",
      defaultValue: "rgba(0, 0, 0, 0.5)",
    },
    {
      header: "Card / panel fill",
      fieldName: "panel_color",
      value: "rgba(255, 255, 255, 0.2)",
      valueType: "string",
      filedType: "RGBA",
      defaultValue: "rgba(255, 255, 255, 0.2)",
    },
    {
      header: "Panel fill below 768px (Right panel only)",
      fieldName: "panel_color_mobile",
      value: "",
      valueType: "string",
      filedType: "RGBA",
      placeholder: "Blank uses the panel fill above",
      description: "The panel covers the whole frame on a phone, so the reference makes it translucent.",
      defaultValue: "",
    },
    {
      header: "Panel width % from 768px (Right panel only)",
      fieldName: "panel_width",
      value: 40,
      valueType: "number",
      filedType: "INPUT",
      placeholder: "40",
      description: "The artwork is sized to the remainder, so the panel sits beside the photo rather than over it.",
      defaultValue: 40,
    },
    {
      header: "Heading",
      fieldName: "title_1",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Online MBA",
      defaultValue: "",
    },
    {
      header: "Heading colour",
      fieldName: "title_1_color",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FFFFFF",
    },
    {
      header: "Sub-heading",
      fieldName: "title",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Master of Business Administration",
      defaultValue: "",
    },
    {
      header: "Sub-heading colour",
      fieldName: "title_color",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FFFFFF",
    },
    {
      header: "Description",
      fieldName: "description",
      value: "",
      valueType: "string",
      filedType: "TEXT_AREA",
      rows: 2,
      defaultValue: "",
    },
    {
      header: "Description colour",
      fieldName: "description_color",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FFFFFF",
    },
    {
      header: "Chips (comma separated)",
      fieldName: "chips",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Limited spots!, Admissions Open",
      defaultValue: "",
    },
    {
      header: "Chip fill",
      fieldName: "chip_color",
      value: "#EFAC6A",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#EFAC6A",
    },
    {
      header: "Chip text colour",
      fieldName: "chip_text_color",
      value: "#031F44",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#031F44",
    },
    {
      header: "Note under the CTAs",
      fieldName: "info_bar",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Admission open for Jul'26 batch",
      defaultValue: "",
    },
    {
      header: "Note fill",
      fieldName: "info_bar_color",
      value: "rgba(255, 255, 255, 0.16)",
      valueType: "string",
      filedType: "RGBA",
      defaultValue: "rgba(255, 255, 255, 0.16)",
    },
    {
      header: "Note text colour",
      fieldName: "info_bar_text_color",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FFFFFF",
    },
    {
      header: "Apply fill (this slide)",
      fieldName: "cta_background_color",
      value: "",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Blank uses the section's",
      defaultValue: "",
    },
    {
      header: "Apply label colour (this slide)",
      fieldName: "cta_text_color",
      value: "",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Blank uses the section's",
      defaultValue: "",
    },
    {
      header: "Brochure fill (this slide)",
      fieldName: "brochure_background_color",
      value: "",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Blank uses the section's",
      defaultValue: "",
    },
    {
      header: "Brochure label colour (this slide)",
      fieldName: "brochure_text_color",
      value: "",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Blank uses the section's",
      defaultValue: "",
    },
    {
      header: "Show accreditation",
      fieldName: "show_accreditation",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
    {
      header: "Priority",
      fieldName: "priority",
      value: 1,
      valueType: "number",
      filedType: "INPUT",
      defaultValue: 1,
    },
    {
      header: "Status",
      fieldName: "status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
  ],

  METADATA_FIELDS_1: [
    {
      header: "Badge image",
      fieldName: "meta_image",
      value: "",
      valueType: "string",
      filedType: "FILE",
      defaultValue: "",
    },
    {
      header: "Badge alt",
      fieldName: "meta_alt_image",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "",
    },
    {
      header: "Label",
      fieldName: "meta_title",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. UGC Entitled",
      description: "Below 768px only the first word is shown, as in the reference.",
      defaultValue: "",
    },
    {
      header: "Priority",
      fieldName: "meta_priority",
      value: 1,
      valueType: "number",
      filedType: "INPUT",
      defaultValue: 1,
    },
    {
      header: "Status",
      fieldName: "meta_status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
  ],
};
